import { useEffect, useState } from "react";
import type { DashboardResponse } from "@atlas/contracts";
import { ApiError, fetchDashboard } from "../lib/api";
import { formatNumberFa, formatToman } from "../lib/format";
import { Icon } from "./Icon";
import { LoadingState, ErrorState } from "./StateViews";

const COMING_SOON = ["یادگیری", "سلامت", "موزیک", "روابط"];

interface MoreHubProps {
  onNavigate: (path: string) => void;
  onLogout: () => void;
  offlinePreview: boolean;
  onToggleOfflinePreview: () => void;
}

export function MoreHub({ onNavigate, onLogout, offlinePreview, onToggleOfflinePreview }: MoreHubProps) {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDashboard(controller.signal)
      .then(setDashboard)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "بارگذاری با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadToken]);

  const financeSummary = !dashboard
    ? "…"
    : dashboard.finance.available
      ? `این ماه خالص ${formatToman(dashboard.finance.netToman)}`
      : "در دسترس نیست";

  const projectsSummary = !dashboard
    ? "…"
    : dashboard.projects.available
      ? `${formatNumberFa(dashboard.projects.total)} مورد`
      : "در دسترس نیست";

  return (
    <section>
      <header className="page-header">
        <h1 className="page-header__title">بیشتر</h1>
      </header>

      {loading && !dashboard && <LoadingState />}
      {error && <ErrorState message={error} onRetry={() => setReloadToken((t) => t + 1)} />}

      {!error && (
        <div className="more-list">
          <button type="button" className="more-row" onClick={() => onNavigate("/more/finance")}>
            <span className="overview-row__icon">
              <Icon name="wallet" width={16} height={16} aria-hidden="true" />
            </span>
            <span className="more-row__body">
              <span className="more-row__label">مالی</span>
              <span className="more-row__sub">{financeSummary}</span>
            </span>
            <Icon name="chevron-right" width={14} height={14} aria-hidden="true" />
          </button>
          <button type="button" className="more-row" onClick={() => onNavigate("/more/projects")}>
            <span className="overview-row__icon">
              <Icon name="flag" width={16} height={16} aria-hidden="true" />
            </span>
            <span className="more-row__body">
              <span className="more-row__label">پروژه‌ها</span>
              <span className="more-row__sub">{projectsSummary}</span>
            </span>
            <Icon name="chevron-right" width={14} height={14} aria-hidden="true" />
          </button>
          <button type="button" className="more-row" onClick={() => onNavigate("/more/shopping")}>
            <span className="overview-row__icon">
              <Icon name="shopping-bag" width={16} height={16} aria-hidden="true" />
            </span>
            <span className="more-row__body">
              <span className="more-row__label">لیست خرید</span>
              <span className="more-row__sub">در دسترس نیست</span>
            </span>
            <Icon name="chevron-right" width={14} height={14} aria-hidden="true" />
          </button>
        </div>
      )}

      <p className="section-heading">پیش‌نمایش حالت‌ها</p>
      <button type="button" className="more-toggle-row" onClick={onToggleOfflinePreview} aria-pressed={offlinePreview}>
        <span>نمایش حالت ارتباط قطع</span>
        <span className={offlinePreview ? "badge badge--danger" : "badge"}>{offlinePreview ? "فعال" : "غیرفعال"}</span>
      </button>

      <p className="section-heading">در دست ساخت</p>
      <div className="more-list">
        {COMING_SOON.map((label) => (
          <div key={label} className="more-row more-row--soon">
            <span className="more-row__icon-placeholder" aria-hidden="true" />
            <span className="more-row__body">
              <span className="more-row__label">{label}</span>
            </span>
            <span className="soon-badge">به‌زودی</span>
          </div>
        ))}
      </div>

      <button type="button" className="logout-link" onClick={onLogout}>
        خروج از حساب
      </button>
    </section>
  );
}
